/**
 * Shared subscription state helpers.
 *
 * Single source of truth for subscription status/plan derivation.
 * Both useSubscription hook and avatarProvider import these instead of
 * implementing their own checks (cf. avatarProvider.ts:72 drift que
 * escapou no PR-2A — resolvido em PR-2C, prevenido aqui em PR-2E).
 */

export type Status = "active" | "past_due" | "canceled" | "pending";
export type Plan = "free" | "premium" | "pro" | "ultra";

/**
 * Returns true if subscription status grants feature access.
 * Active + past_due both grant (past_due = grace period before canceled).
 */
export const isActiveStatus = (s: Status): boolean =>
  s === "active" || s === "past_due";

/**
 * Returns true if plan is paid (Premium, Pro or Ultra). Free returns false.
 */
export const isPremiumPlan = (p: Plan): boolean =>
  p === "premium" || p === "pro" || p === "ultra";

/**
 * Returns true if plan grants Pro-tier access. Ultra is the top tier and
 * supersets Pro, so it counts as Pro access too.
 */
export const isProPlan = (p: Plan): boolean =>
  p === "pro" || p === "ultra";

/**
 * Postgres timestamptz often serializes as "2099-12-31 23:59:59+00" (space
 * separator, abbreviated offset). WebKit (iOS Safari / Capacitor WKWebView)
 * is stricter than V8 on Date.parse and returns NaN for that exact shape,
 * which silently dropped iOS Premium users to `hasNotExpired === false`
 * even with a correct DB row (root cause of P0 #3). Normalize to ISO 8601:
 * space → 'T', and "+00" / "+0000" → "+00:00".
 */
const normalizeTimestamp = (end: string): string => {
  let s = end.replace(" ", "T");
  s = s.replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3"); // "+0000" → "+00:00"
  s = s.replace(/([+-])(\d{2})$/, "$1$2:00");        // "+00"   → "+00:00"
  return s;
};

/**
 * Returns true if `end` (ISO string) is strictly in the future. NULL /
 * empty / unparseable values count as expired — Free rows typically have
 * a NULL period_end, but `isPremiumPlan` short-circuits before this is
 * consulted, so a NULL here is harmless for free users. Unparseable values
 * are reported to Sentry; we conservatively treat them as expired rather
 * than granting free access on a parse glitch.
 */
export const hasNotExpired = (end: string | null | undefined): boolean => {
  if (!end) return false;
  const t = Date.parse(normalizeTimestamp(end));
  if (Number.isNaN(t)) {
    console.error("[subscriptionHelpers] unparseable current_period_end:", end);
    return false;
  }
  return t > Date.now();
};

/**
 * Returns true if user has active paid access.
 *
 * Rule (21/05): plan must be paid AND current_period_end must be in the
 * future. Status is intentionally ignored — a `canceled` row whose period
 * still has runway keeps access until current_period_end passes (this is
 * the standard "cancels at period end" semantics and avoids the refund
 * dispute path). Conversely, a PIX-non-renewal row stuck on
 * status='active' loses access the moment its period_end passes.
 */
export const isPremiumActive = (
  p: Plan,
  end: string | null | undefined,
): boolean => isPremiumPlan(p) && hasNotExpired(end);
